import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import ContentBox from 'components/CncUI/ContentBox';
import Typography from 'components/CncUI/Typography';
import TextField from 'components/CncUI/TextField';
import Button from 'components/CncUI/Button';
import Alert from 'components/CncUI/Alert';

import Grid from '@material-ui/core/Grid';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import IconButton from '@material-ui/core/IconButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import LeftArrow from '@material-ui/icons/KeyboardArrowLeftRounded';
import ValidationCircle from '../ValidationCircle';

import useSetUserPassword from '../hooks/useSetUserPassword';
import passwordValidator, { VALIDATORS } from 'utilities/passwordValidator';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    width: 474,
    margin: '50px auto',
    padding: '40px 69px 0',
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 0,
      padding: '29px 20px 0',
    },
  },
  tips: {
    boxShadow: 'none',
    '&.Mui-expanded': {
      margin: '8px 0',
    },
  },
  summary: {
    minHeight: 0,
    '&.Mui-expanded': {
      minHeight: 0,
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
  },
  tipsDetails: {
    textAlign: 'left',
    backgroundColor: palette.cncUI.default.table,
    padding: '11px 23px',
    '& p': {
      color: '#7e7e7e !important',
    },
    '& span': {
      fontWeight: 700,
    },
  },
  tipFocus: {
    '& svg': {
      verticalAlign: 'middle',
      transform: 'rotate(180deg)',
    },
    width: '100%',
    color: `${palette.cncUI.default.primary} !important`,
  },
  tipNormal: {
    '& svg': {
      verticalAlign: 'middle',
    },
    width: '100%',
    color: `${palette.cncUI.primary.gray['+0']} !important`,
  },
  hidden: {
    display: 'none',
  },
  validation: {
    textAlign: 'left',
    padding: 0,
    flexDirection: 'column',
    '& svg': {
      verticalAlign: 'middle',
      width: '0.8em',
      height: '0.8em',
    },
  },
  button: {
    marginTop: 50,
    marginBottom: 50,
    [breakpoints.down('sm')]: {
      marginTop: 20,
      marginBottom: 50,
    },
  },
  leftArrow: {
    borderRadius: '50%',
    backgroundColor: `${palette.cncUI.default.background} !important`,
    color: palette.secondary.main,
    marginRight: 8,
  },
  backButton: {
    marginTop: 0,
    marginBottom: 69,
    [breakpoints.down('sm')]: {
      marginBottom: 100,
    },
  },
  passwordFieldContainer: {
    marginTop: 20,
  },
  error: {
    width: 344,
    marginBottom: 16,
    marginTop: 8,
    fontWeight: 700,
    alignSelf: 'center',
  },
}));

const CreatePassword = props => {
  const classes = useStyles();
  const { setStep, state: stepState, skipMfa } = props;
  const { tokenData = {} } = stepState;
  const { user = {}, policy = '{"complexity":{"minLength":12}}' } = tokenData;
  const { complexity = { minLength: 12 } } = JSON.parse(policy);
  const { firstName = '', lastName = '', email = '' } = user;
  const setUserPassword = useSetUserPassword();
  const [state, setState] = useState({
    tips: false,
    validation: false,
    disabled: false,
    password: '',
    showPassword: false,
    confirmPassword: '',
    showConfirmPassword: false,
    error: '',
  });
  const modify = data => setState(old => ({ ...old, ...data }));

  const handleChange = (panel, open) => {
    if (panel === 'tips') {
      modify({ tips: !state.tips });
    } else {
      modify({ validation: open || !state.validation });
    }
  };

  const handleClickShowPassword = whichPassword => {
    if (whichPassword === 'confirm') {
      modify({ showConfirmPassword: !state.showConfirmPassword });
    } else {
      modify({ showPassword: !state.showPassword });
    }
  };

  const handlePasswordChange = prop => event => {
    modify({ [prop]: event.target.value });
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const validators = {
    length: VALIDATORS.length(complexity.minLength),
    contains: VALIDATORS.contains(firstName, email, lastName),
  };
  if (complexity.minLowerCase && complexity.minLowerCase === 1) {
    validators.lowercase = VALIDATORS.lowercase;
  }
  if (complexity.minUpperCase && complexity.minUpperCase === 1) {
    validators.uppercase = VALIDATORS.uppercase;
  }
  if (complexity.minNumber && complexity.minNumber === 1) {
    validators.number = VALIDATORS.number;
  }
  if (complexity.minSymbol && complexity.minSymbol === 1) {
    validators.symbol = VALIDATORS.symbol;
  }

  const nextDisabled = !(
    passwordValidator(Object.values(validators), state.password) &&
    state.password === state.confirmPassword
  );
  const isError =
    state.confirmPassword.length > 0 &&
    state.password !== state.confirmPassword;

  const handleCreate = () => {
    modify({ disabled: true });
    setUserPassword(tokenData.stateToken, state.password)
      .then(() => {
        setStep(skipMfa ? 'AccountCreated' : 'VerifyIdentity');
      })
      .catch(err => {
        modify({ error: err.message, disabled: false });
      });
  };

  return (
    <ContentBox className={classes.root} id="CreatePasswordRoot">
      <Grid container spacing={2} direction="column" align="center">
        <Grid item xs={12}>
          <Typography id="CreatePasswordTitle" variant="h3">
            Create your password
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography id="CreatePasswordDescription" variant="body2">
            Password should be at least {complexity.minLength || 12} characters.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Accordion
            id="CreatePasswordTips"
            className={classes.tips}
            style={{ marginBottom: 20 }}
            expanded={state.tips === true}
            onChange={() => handleChange('tips')}
          >
            <AccordionSummary className={classes.summary}>
              <Typography
                noMargin
                variant="body2"
                className={
                  state.tips === true ? classes.tipFocus : classes.tipNormal
                }
              >
                View tips for remembering your password <ExpandMoreIcon />
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.tipsDetails}>
              <Typography variant="body2">
                Try using words and numbers that are meaningful to you.
                <br />
                <br />
                For example, you can use your favorite color and your pet's name
                with a year or zip code you'll remember, such as:
                <span> RoyalBlue_Spot96005 </span>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
        {state.error && (
          <Grid item xs={12} className={classes.error}>
            <Alert fullWidth error>
              {state.error.replace('GraphQL error: ', '')}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} style={{ width: '100%' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="CreatePasswordPasswordInput"
                type={state.showPassword ? 'text' : 'password'}
                value={state.password}
                placeholder="Enter new password"
                onChange={handlePasswordChange('password')}
                onFocus={() => handleChange('validation', true)}
                autoComplete="new-password"
                label="New Password"
                endIcon={
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword('password')}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {state.showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                }
              />

              <Accordion
                id="CreatePasswordValidation"
                className={classes.tips}
                expanded={state.validation === true}
                onChange={() => handleChange('validation')}
              >
                <AccordionSummary className={classes.hidden}>
                  {' '}
                </AccordionSummary>
                <AccordionDetails className={classes.validation}>
                  <ValidationCircle
                    passwordLength={state.password.length}
                    id="CreatePasswordLength"
                    validate={validators.length(state.password)}
                    label={`At least ${complexity.minLength || 12} characters`}
                  />
                  {validators.lowercase && (
                    <ValidationCircle
                      passwordLength={state.password.length}
                      id="CreatePasswordLowercase"
                      validate={validators.lowercase(state.password)}
                      label="Contains lowercase characters"
                    />
                  )}
                  {validators.uppercase && (
                    <ValidationCircle
                      passwordLength={state.password.length}
                      id="CreatePasswordUppercase"
                      validate={validators.uppercase(state.password)}
                      label="Contains uppercase characters"
                    />
                  )}
                  {validators.number && (
                    <ValidationCircle
                      passwordLength={state.password.length}
                      id="CreatePasswordNumber"
                      validate={validators.number(state.password)}
                      label="Includes a number"
                    />
                  )}
                  {validators.symbol && (
                    <ValidationCircle
                      passwordLength={state.password.length}
                      id="CreatePasswordSymbol"
                      validate={validators.symbol(state.password)}
                      label="Includes a symbol"
                    />
                  )}
                  <ValidationCircle
                    passwordLength={state.password.length}
                    id="CreatePasswordContains"
                    validate={validators.contains(state.password)}
                    label="Cannot contain parts of your name or email address"
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12} className={classes.passwordFieldContainer}>
              <TextField
                fullWidth
                id="CreatePasswordConfirmPassword"
                type={state.showConfirmPassword ? 'text' : 'password'}
                value={state.confirmPassword}
                label="Confirm Password"
                placeholder="Re-type password"
                autoComplete="new-password"
                onChange={handlePasswordChange('confirmPassword')}
                onEnter={handleCreate}
                error={isError}
                helperText={isError ? 'Passwords do not match' : ''}
                endIcon={
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword('confirm')}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {state.showConfirmPassword ? (
                      <Visibility />
                    ) : (
                      <VisibilityOff />
                    )}
                  </IconButton>
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Button
            fullWidth
            id="CreatePasswordCreatePasswordButton"
            disabled={state.disabled || nextDisabled}
            onClick={handleCreate}
            className={classes.button}
          >
            Create password
          </Button>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Button
            tertiary
            id="CreatePasswordBackButton"
            className={classes.backButton}
            onClick={() => setStep('Welcome')}
          >
            <LeftArrow className={classes.leftArrow} />
            Back
          </Button>
        </Grid>
      </Grid>
    </ContentBox>
  );
};

export default CreatePassword;
